/**
 * Add / edit a roster row (AC-ROSTER-001, AC-ROSTER-003).
 *
 * Opens in a dialog over the roster table (the table is the page's main
 * content). The "Add cohort value" modal stacks on top of it as a sibling
 * overlay; the player dialog ignores keys while focus is inside it.
 *
 * The accepted field list is exactly the one `POST /players/v1` accepts —
 * first/last name, cohort, jersey number, position, school. There is no
 * date-of-birth or age input, and the service rejects one outright, so the
 * omission cannot drift back in silently.
 *
 * Validation runs twice: inline here before any request, and again on the
 * server, whose `details.field` is mapped back onto the offending control.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { OverlayDialog } from '../../components/overlay-dialog';
import { TextField } from '../../components/text-field';
import { UnsavedChangesGuard, useFormDirty } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import type { CohortValue, Player, PlayerInput } from '../../lib/api-types';

import { AddCohortValueModal } from './add-cohort-value-modal';
import { CohortSelect } from './cohort-select';

const NAME_MAX_LENGTH = 128;
const JERSEY_NUMBER_MAX_LENGTH = 5;
const JERSEY_NUMBER_PATTERN = /^[A-Za-z0-9]+$/;
const POSITION_MAX_LENGTH = 32;
const SCHOOL_MAX_LENGTH = 128;

export interface PlayerDraft {
  firstName: string;
  lastName: string;
  cohort: string;
  jerseyNumber: string;
  position: string;
  school: string;
}

const EMPTY_DRAFT: PlayerDraft = {
  firstName: '',
  lastName: '',
  cohort: '',
  jerseyNumber: '',
  position: '',
  school: '',
};

function toDraft(player: Player | null): PlayerDraft {
  if (player === null) return EMPTY_DRAFT;
  return {
    firstName: player.first_name,
    lastName: player.last_name,
    cohort: player.cohort,
    jerseyNumber: player.jersey_number ?? '',
    position: player.position ?? '',
    school: player.school ?? '',
  };
}

/** Field → message. Empty means the draft may be submitted. */
export function validatePlayerDraft(draft: PlayerDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.firstName.trim() === '') errors.first_name = 'Enter a first name.';
  else if (draft.firstName.trim().length > NAME_MAX_LENGTH) {
    errors.first_name = `Use at most ${NAME_MAX_LENGTH} characters.`;
  }
  if (draft.lastName.trim() === '') errors.last_name = 'Enter a last name.';
  else if (draft.lastName.trim().length > NAME_MAX_LENGTH) {
    errors.last_name = `Use at most ${NAME_MAX_LENGTH} characters.`;
  }
  if (draft.cohort.trim() === '') {
    errors.cohort = 'Choose a cohort — every player needs one.';
  }
  const jersey = draft.jerseyNumber.trim();
  if (jersey !== '' && !JERSEY_NUMBER_PATTERN.test(jersey)) {
    errors.jersey_number = 'Use letters and digits only.';
  } else if (jersey.length > JERSEY_NUMBER_MAX_LENGTH) {
    errors.jersey_number = `Use at most ${JERSEY_NUMBER_MAX_LENGTH} characters.`;
  }
  if (draft.position.trim().length > POSITION_MAX_LENGTH) {
    errors.position = `Use at most ${POSITION_MAX_LENGTH} characters.`;
  }
  if (draft.school.trim().length > SCHOOL_MAX_LENGTH) {
    errors.school = `Use at most ${SCHOOL_MAX_LENGTH} characters.`;
  }
  return errors;
}

/** `null` clears an optional column; the service treats `''` the same way. */
function toPlayerInput(draft: PlayerDraft): PlayerInput {
  return {
    first_name: draft.firstName.trim(),
    last_name: draft.lastName.trim(),
    cohort: draft.cohort.trim(),
    jersey_number: draft.jerseyNumber.trim() === '' ? null : draft.jerseyNumber.trim(),
    position: draft.position.trim() === '' ? null : draft.position.trim(),
    school: draft.school.trim() === '' ? null : draft.school.trim(),
  };
}

export interface PlayerFormProps {
  /** `null` → create mode. */
  player: Player | null;
  cohortValues: readonly CohortValue[];
  canAddCohortValue?: boolean;
  onCohortValueCreated?: (value: CohortValue) => void;
  /** Performs the POST/PATCH and resolves with the saved row. */
  onSave: (input: PlayerInput) => Promise<Player>;
  /** `isAnother` is true when the admin chose "Save & add another". */
  onSaved: (player: Player, options: { isAnother: boolean }) => void;
  onCancel: () => void;
}

export function PlayerForm({
  player,
  cohortValues,
  canAddCohortValue = false,
  onCohortValueCreated,
  onSave,
  onSaved,
  onCancel,
}: PlayerFormProps): JSX.Element {
  const isEditing = player !== null;
  const [draft, setDraft] = useState<PlayerDraft>(() => toDraft(player));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The dialog owns a <form>, so it is rendered as a SIBLING of this form —
  // nested forms are invalid HTML and the inner submit would bubble out.
  const [isAddingCohortValue, setIsAddingCohortValue] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // Half-typed player details must survive a mis-click on the nav (Task 38).
  const { isDirty, reset: resetDirty } = useFormDirty(formRef);

  // Switching between "add" and "edit <player>" re-seeds the controls and puts
  // the caret where the user expects it.
  useEffect(() => {
    setDraft(toDraft(player));
    setFieldErrors({});
    setFormError(null);
    firstFieldRef.current?.focus();
  }, [player]);

  function update(patch: Partial<PlayerDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }

  async function submit(isAnother: boolean): Promise<void> {
    const errors = validatePlayerDraft(draft);
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    try {
      const saved = await onSave(toPlayerInput(draft));
      if (isAnother) setDraft({ ...EMPTY_DRAFT, cohort: draft.cohort, school: draft.school });
      // Saved work is no longer at risk: re-baseline before the parent navigates.
      resetDirty();
      onSaved(saved, { isAnother });
      firstFieldRef.current?.focus();
    } catch (caught) {
      if (ApiError.isApiError(caught)) {
        setFormError(caught.message);
        if (caught.field !== null) setFieldErrors({ [caught.field]: caught.message });
      } else {
        setFormError('The player could not be saved. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void submit(false);
  }

  const title = isEditing ? `Edit ${player.full_name}` : 'Add Player';

  return (
    <>
      <OverlayDialog title={title} onClose={onCancel} panelClassName="roster-form-dialog">
        <UnsavedChangesGuard isDirty={isDirty} />

        <form className="stack roster-form" ref={formRef} onSubmit={handleSubmit} noValidate>
          <p className="muted roster-form__note">
            Program data only — names, cohort, jersey, position and school. Never enter dates of
            birth, contact details, guardian information or medical notes.
          </p>
          <div className="desk-form desk-form--stacked desk-form--two-col roster-form__grid">
            <TextField
              ref={firstFieldRef}
              label="First name"
              name="first_name"
              required
              autoComplete="off"
              value={draft.firstName}
              onChange={(event) => update({ firstName: event.target.value })}
              error={fieldErrors.first_name ?? null}
            />
            <TextField
              label="Last name"
              name="last_name"
              required
              autoComplete="off"
              value={draft.lastName}
              onChange={(event) => update({ lastName: event.target.value })}
              error={fieldErrors.last_name ?? null}
            />
            <div className="desk-form__full">
              <CohortSelect
                value={draft.cohort}
                onChange={(code) => update({ cohort: code })}
                values={cohortValues}
                error={fieldErrors.cohort ?? null}
                canAddValue={canAddCohortValue}
                onAddValueRequested={() => setIsAddingCohortValue(true)}
                isDisabled={isSubmitting}
              />
            </div>
            <TextField
              label="Jersey number"
              name="jersey_number"
              inputMode="numeric"
              autoComplete="off"
              value={draft.jerseyNumber}
              onChange={(event) => update({ jerseyNumber: event.target.value })}
              error={fieldErrors.jersey_number ?? null}
              hint="Optional. Leading zeros are kept."
            />
            <TextField
              label="Position"
              name="position"
              autoComplete="off"
              value={draft.position}
              onChange={(event) => update({ position: event.target.value })}
              error={fieldErrors.position ?? null}
              hint="Optional. Example: QB, WR, LB."
            />
            <TextField
              label="School"
              name="school"
              autoComplete="off"
              value={draft.school}
              onChange={(event) => update({ school: event.target.value })}
              error={fieldErrors.school ?? null}
              hint="Optional."
            />
          </div>

          {formError !== null ? (
            <div className="banner banner--error" role="alert">
              <p>{formError}</p>
            </div>
          ) : null}

          <div className="btn-row dialog-actions">
            <button type="button" className="btn btn--secondary" onClick={onCancel}>
              Cancel
            </button>
            {isEditing ? null : (
              <button
                type="button"
                className="btn btn--ghost"
                disabled={isSubmitting}
                onClick={() => void submit(true)}
              >
                Save &amp; add another
              </button>
            )}
            <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Save Player'}
            </button>
          </div>
        </form>
      </OverlayDialog>

      {isAddingCohortValue ? (
        <AddCohortValueModal
          existingCodes={cohortValues.map((entry) => entry.code)}
          onCancel={() => setIsAddingCohortValue(false)}
          onCreated={(created) => {
            onCohortValueCreated?.(created);
            update({ cohort: created.code });
            setFieldErrors(({ cohort: _cohort, ...rest }) => rest);
            setIsAddingCohortValue(false);
          }}
        />
      ) : null}
    </>
  );
}
